import { router, usePage } from '@inertiajs/vue3';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { nextTick } from 'vue';
import './tour.css';

const STORAGE_KEY = 'tour:pending';
// Separate from STORAGE_KEY: that one is a short-lived handoff consumed the
// moment a page finishes navigating. This one can sit untouched for as
// long as the user wants to click around and test the app before resuming.
const PAUSED_KEY = 'tour:paused';
const RESUME_BTN_ID = 'tour-resume-btn';

type TutorialContext = {
    projectId: string;
};

type StoredTourState = {
    stepIndex: number;
    ctx: TutorialContext;
};

type StepDef = {
    element?: string;
    popover: {
        title: string;
        description: string;
        side?: 'top' | 'bottom' | 'left' | 'right';
        align?: 'start' | 'center' | 'end';
        nextBtnText?: string;
    };
    /** URL where this step is shown — used to navigate before displaying it */
    url?: (ctx: TutorialContext) => string;
    /** Action to run when the user clicks "Suivant" from this step */
    onNextAction?: (ctx: TutorialContext) => Promise<Partial<TutorialContext>>;
    /** After onNextAction, reload the current page instead of navigating forward */
    reloadAfterAction?: boolean;
    /** Runs once the step's element is about to be highlighted — used to set up the page's state (e.g. select an item) before the popover shows */
    onHighlightStarted?: () => void;
    /** Show this step's popover centered and enlarged instead of pinned to the corner — for content too big to read at the default size (e.g. the embedded PubMed screenshot). */
    centered?: boolean;
};

const STEPS: StepDef[] = [
    // 0 — Welcome
    {
        popover: {
            title: 'Bienvenue sur Rewio',
            description:
                "Nous allons vous guider à travers les fonctionnalités clés en une trentaine d'étapes.\n\nVous découvrirez comment créer une revue systématique de bout en bout — de la configuration jusqu'à la rédaction.",
            nextBtnText: 'Commencer le tour →',
        },
    },
    // 1 — Projects list
    {
        url: () => '/projects',
        element: '[data-tour="projects-list"]',
        popover: {
            title: 'Vos projets de revue',
            description:
                'Tous vos projets de revue systématique sont listés ici. Chaque projet vous guide à travers les 5 étapes PRISMA.',
        },
    },
    // 2 — Create project button
    {
        element: '[data-tour="create-project"]',
        popover: {
            title: 'Créer un projet',
            description:
                "Cliquez sur « Suivant » pour voir le formulaire de création, déjà pré-rempli avec une revue exemple sur l'IA médicale.",
            nextBtnText: 'Voir le formulaire →',
        },
    },
    // 3 — Project creation form (pre-filled by ?tutorial=1)
    {
        url: () => '/projects/create?tutorial=1',
        element: '[data-tour="project-form"]',
        popover: {
            title: 'Formulaire de création',
            description:
                'Le formulaire est pré-rempli pour ce tutoriel. En réalité, vous saisiriez votre titre de revue et sa description clinique.\n\nCliquez sur « Créer le projet » pour continuer.',
            nextBtnText: 'Créer le projet →',
        },
        onNextAction: async () => {
            disableNextBtn('Création en cours…');
            const res = await postDemo('/tutorial/demo/project');
            const data = (await res.json()) as { project_id: string };

            return { projectId: data.project_id };
        },
    },
    // 4 — Project overview: PRISMA progress
    {
        url: (ctx) => `/projects/${ctx.projectId}`,
        element: '[data-tour="overview-progress"]',
        popover: {
            title: 'Avancement PRISMA',
            description:
                'Cette section suit votre progression à travers les 5 étapes PRISMA : identification, sélection, éligibilité, inclusion et synthèse.\n\nLes chiffres se mettent à jour en temps réel.',
            side: 'bottom',
        },
    },
    // 5 — Project overview: stats
    {
        element: '[data-tour="overview-stats"]',
        popover: {
            title: 'Statistiques du projet',
            description:
                "Articles importés, en cours de décision et inclus. Ces compteurs reflètent l'état actuel de votre base bibliographique.",
            side: 'bottom',
        },
    },
    // 6 — Settings: overview
    {
        url: (ctx) => `/projects/${ctx.projectId}/settings`,
        element: '[data-tour="settings-overview"]',
        popover: {
            title: 'Configuration de la recherche',
            description:
                "Cette page réunit tout le protocole de votre revue : question de recherche, critères d'inclusion/exclusion, bases de données et stratégie.\n\nL'IA peut pré-remplir l'ensemble depuis votre description de projet. Voyons chaque partie en détail.",
        },
    },
    // 7 — Settings: research question
    {
        element: '[data-tour="settings-research-question"]',
        popover: {
            title: 'Question de recherche',
            description:
                "Décrivez la question ou l'objectif de votre revue — c'est le fil conducteur qui guide vos critères, vos équations et vos décisions de screening.",
        },
    },
    // 8 — Settings: inclusion/exclusion criteria
    {
        element: '[data-tour="research-config"]',
        popover: {
            title: "Critères d'inclusion et d'exclusion",
            description:
                "Un critère par ligne. L'aperçu se met à jour au fur et à mesure — ces critères sont ensuite utilisés par l'IA pour justifier ses suggestions de screening.",
            side: 'bottom',
        },
    },
    // 9 — Settings: databases & search strategy
    {
        element: '[data-tour="settings-databases"]',
        popover: {
            title: 'Bases de données et stratégie',
            description:
                'Renseignez les langues et bases de données interrogées (PubMed, Scopus…), puis juste en dessous, votre stratégie de recherche détaillée.',
        },
    },
    // 10 — Equations: intro
    {
        url: (ctx) => `/projects/${ctx.projectId}/search-equations`,
        element: '[data-tour="search-equations"]',
        popover: {
            title: 'Équations de recherche',
            description:
                "Ici vous rédigez une équation par base de données (PubMed, Scopus, Cochrane…). L'assistant peut les générer automatiquement à partir de votre configuration.",
        },
    },
    // 11 — Generate equations button
    {
        element: '[data-tour="generate-equations-btn"]',
        popover: {
            title: 'Génération par IA',
            description:
                "L'IA analyse vos critères et rédige des équations booléennes optimisées pour chaque base.\n\nCliquez sur « Générer » pour simuler la génération.",
            nextBtnText: "Générer avec l'IA →",
        },
        onNextAction: async (ctx) => {
            disableNextBtn('Génération…');
            await postDemo(`/tutorial/demo/equations/${ctx.projectId}`);

            return {};
        },
        reloadAfterAction: true,
    },
    // 12 — Equations result
    {
        element: '[data-tour="equations-result"]',
        popover: {
            title: 'Vos équations générées',
            description:
                "L'IA a rédigé une équation optimisée pour chaque base de données. Chaque bloc affiche la base cible, l'équation booléenne et le nombre estimé de résultats.",
            side: 'bottom',
        },
    },
    // 13 — PubMed → Zotero selection (no live element: external site)
    {
        popover: {
            title: 'Sélectionner vos résultats sur PubMed',
            description:
                'Lancez votre équation dans PubMed, cochez les résultats pertinents, puis cliquez sur l\'icône Zotero de votre navigateur (extension Zotero Connector) : le sélecteur d\'éléments s\'ouvre, validez votre sélection et les articles rejoignent directement votre bibliothèque Zotero.\n<img src="/tutorial/interactive/pubmed-zotero.png" alt="Recherche PubMed avec des résultats cochés et le sélecteur d\'éléments Zotero Connector ouvert" />',
        },
        centered: true,
    },
    // 14 — Connect Zotero
    {
        url: (ctx) => `/projects/${ctx.projectId}/import`,
        element: '[data-tour="zotero-connect-card"]',
        popover: {
            title: 'Connecter votre compte Zotero',
            description:
                "Cliquez sur « Connecter Zotero » : vous êtes redirigé pour autoriser l'accès, puis la connexion reste active pour tous vos projets — plus besoin d'exporter/importer de fichier à la main.",
            side: 'bottom',
        },
    },
    // 15 — Import references from Zotero
    {
        element: '[data-tour="zotero-connect-card"]',
        popover: {
            title: 'Importer vos références',
            description:
                "Une fois connecté, vos collections Zotero apparaissent ici — importez celle de votre choix en un clic.\n\nCliquez sur « Suivant » pour simuler l'import de votre collection de démonstration.",
            side: 'bottom',
            nextBtnText: "Simuler l'import →",
        },
        onNextAction: async (ctx) => {
            disableNextBtn('Import en cours…');
            await postDemo(`/tutorial/demo/articles/${ctx.projectId}`);

            return {};
        },
    },
    // 16 — Articles list
    {
        url: (ctx) => `/projects/${ctx.projectId}/articles`,
        element: '[data-tour="articles-list"]',
        popover: {
            title: 'Articles importés',
            description:
                "12 articles ont été importés depuis votre bibliothèque Zotero. Vous pouvez les filtrer, les rechercher et prendre des décisions d'inclusion/exclusion.",
            side: 'right',
        },
    },
    // 17 — Screening panel
    {
        element: '[data-tour="articles-screening"]',
        popover: {
            title: 'Panneau de criblage',
            description:
                "Cliquez sur un article pour voir son résumé, son DOI et les PDFs associés. Vous pouvez l'inclure, l'exclure ou le marquer comme douteux.",
            side: 'left',
        },
    },
    // 18 — AI suggestions button
    {
        element: '[data-tour="ai-suggest-btn"]',
        popover: {
            title: 'Screening assisté par IA',
            description:
                "L'assistant analyse chaque résumé et propose une décision d'inclusion ou d'exclusion avec un raisonnement détaillé.\n\nCliquez pour simuler l'analyse sur les 12 articles.",
            nextBtnText: 'Lancer le screening IA →',
        },
        onNextAction: async (ctx) => {
            disableNextBtn('Analyse en cours…');
            await postDemo(`/tutorial/demo/screening/${ctx.projectId}`);

            return {};
        },
        reloadAfterAction: true,
    },
    // 19 — After AI suggestions visible
    {
        element: '[data-tour="articles-screening"]',
        popover: {
            title: "C'est vous qui décidez",
            description:
                'Les suggestions IA apparaissent dans le panneau — confiance, décision proposée et justification.\n\nVous pouvez les accepter ou les ignorer. La décision finale vous appartient toujours.',
        },
        onHighlightStarted: selectFirstArticle,
    },
    // 20 — Manual PDF import
    {
        url: (ctx) => `/projects/${ctx.projectId}/import`,
        element: '[data-tour="pdf-upload-section"]',
        popover: {
            title: 'Ajouter vos PDF manquants',
            description:
                "Zotero n'a pas toujours le PDF en pièce jointe. Depuis cette même page, déposez directement vos fichiers PDF — ils rejoignent votre bibliothèque, prêts à être associés aux articles.\n\nCliquez sur « Suivant » pour simuler l'ajout de 3 PDF.",
            side: 'bottom',
            nextBtnText: "Simuler l'ajout de PDF →",
        },
        onNextAction: async (ctx) => {
            disableNextBtn('Ajout en cours…');
            await postDemo(`/tutorial/demo/pdfs/${ctx.projectId}`);

            return {};
        },
    },
    // 21 — Manual PDF linking
    {
        url: (ctx) => `/projects/${ctx.projectId}/associations`,
        element: '[data-tour="associations-right-panel"]',
        popover: {
            title: 'Lier un PDF manuellement',
            description:
                "Chaque PDF déposé peut être lié à la main : sélectionnez un fichier de la bibliothèque et associez-le à l'article correspondant.",
        },
        onHighlightStarted: selectFirstAssociationPaper,
    },
    // 22 — Auto-match button (subscription feature)
    {
        element: '[data-tour="auto-match-btn"]',
        popover: {
            title: 'Détection automatique (abonnement)',
            description:
                "Ce bouton associe automatiquement vos PDF restants à partir du DOI, du titre ou d'une empreinte numérique — une fonctionnalité réservée aux plans avec abonnement IA.\n\nCliquez sur « Suivant » pour simuler la détection sur les PDF restants.",
            nextBtnText: 'Simuler la détection →',
        },
        onNextAction: async (ctx) => {
            disableNextBtn('Détection en cours…');
            await postDemo(`/tutorial/demo/pdfs-auto-match/${ctx.projectId}`);

            return {};
        },
    },
    // 23 — Back to the article list: PDF-linked badge
    {
        url: (ctx) => `/projects/${ctx.projectId}/articles`,
        element: '[data-tour="articles-list"]',
        popover: {
            title: 'Statut de liaison PDF',
            description:
                "Chaque article affiche désormais un badge « PDF » vert s'il est lié, ou « PDF manquant » en orange sinon. Un coup d'œil suffit pour repérer ce qu'il reste à associer.",
            side: 'right',
        },
    },
    // 24 — Extraction grid intro
    {
        url: (ctx) => `/projects/${ctx.projectId}/data`,
        element: '[data-tour="extraction-grid"]',
        popover: {
            title: "Grille d'extraction",
            description:
                "Pour chaque article inclus, extrayez les données clés dans un tableau structuré. Les colonnes sont définissables librement : texte, nombre, sélection…\n\n⚠️ Un PDF doit être lié à l'article pour que l'IA lise le texte complet — sans PDF, elle ne travaille qu'à partir du titre et du résumé.",
        },
    },
    // 25 — Generate extraction grid
    {
        element: '[data-tour="generate-grid-btn"]',
        popover: {
            title: 'Extraction automatique par IA',
            description:
                "L'IA lit les PDFs et remplit automatiquement chaque cellule d'après le contenu des articles.\n\nCliquez pour simuler l'extraction sur les articles inclus.",
            nextBtnText: "Lancer l'extraction →",
        },
        onNextAction: async (ctx) => {
            disableNextBtn('Extraction…');
            await postDemo(`/tutorial/demo/extraction/${ctx.projectId}`);

            return {};
        },
        reloadAfterAction: true,
    },
    // 26 — Extraction cells visible
    {
        element: '[data-tour="extraction-cells"]',
        popover: {
            title: 'Données extraites',
            description:
                "Les cellules remplies par l'IA sont mises en évidence — à vous de les valider ou les corriger.\n\nUne fois la grille complète, direction le diagramme PRISMA puis la rédaction.",
        },
    },
    // 27 — PRISMA diagram
    {
        url: (ctx) => `/projects/${ctx.projectId}/prisma-stats`,
        element: '[data-tour="prisma-diagram"]',
        popover: {
            title: 'Diagramme PRISMA',
            description:
                'Ce diagramme se génère automatiquement à partir de vos décisions de screening. Il suit le standard PRISMA 2020 et peut être exporté en PNG ou PDF pour vos publications.',
            side: 'right',
            nextBtnText: 'Passer à la rédaction →',
        },
        onNextAction: async (ctx) => {
            disableNextBtn('Préparation…');
            await postDemo(`/tutorial/demo/document-content/${ctx.projectId}`);

            return {};
        },
    },
    // 28 — Editor canvas
    {
        url: (ctx) => `/projects/${ctx.projectId}/editor`,
        element: '[data-tour="editor-canvas"]',
        popover: {
            title: 'Éditeur de synthèse',
            description:
                "Rédigez votre revue dans cet éditeur collaboratif : formatage riche, numérotation automatique des sections et synchronisation en temps réel.\n\nUn brouillon a déjà été rédigé pour cette démo — découvrons ensemble les outils d'insertion : diagramme PRISMA, grille d'extraction et saut de page.",
            side: 'right',
        },
    },
    // 29 — Insert PRISMA diagram
    {
        element: '[data-tour="editor-insert-prisma"]',
        popover: {
            title: 'Insérer le diagramme PRISMA',
            description:
                'Ce bouton insère directement le diagramme PRISMA à jour dans votre texte, sans capture d\'écran ni copier-coller.\n<div class="tour-step-badge">✅ Diagramme inséré dans le texte</div>',
            side: 'bottom',
        },
        onHighlightStarted: () =>
            clickTourButton('[data-tour="editor-insert-prisma"]'),
    },
    // 30 — Insert extraction grid
    {
        element: '[data-tour="editor-insert-extraction"]',
        popover: {
            title: "Insérer la grille d'extraction",
            description:
                'Insérez tout ou partie de votre grille d\'extraction sous forme de tableau formaté, prêt à être ajusté dans le texte.\n<div class="tour-step-badge">✅ Tableau inséré dans le texte</div>',
            side: 'bottom',
        },
        onHighlightStarted: () => {
            clickTourButton('[data-tour="editor-insert-extraction"]');
            // Let Vue mount the dialog and pre-select its columns before confirming.
            window.setTimeout(() => {
                clickTourButton('[data-tour="extraction-dialog-confirm"]');
            }, 250);
        },
    },
    // 31 — Page break
    {
        element: '[data-tour="editor-page-break"]',
        popover: {
            title: 'Saut de page',
            description:
                'Structurez votre document en insérant des sauts de page — pratique avant chaque nouvelle section pour l\'export final.\n<div class="tour-step-badge">✅ Saut de page ajouté</div>',
            side: 'bottom',
        },
        onHighlightStarted: () => {
            dismissAnyOpenDialog();
            clickTourButton('[data-tour="editor-page-break"]');
        },
    },
    // 32 — Citation feature
    {
        element: '[data-tour="editor-citation"]',
        popover: {
            title: 'Citations automatiques',
            description:
                'Dans le panneau de droite, cliquez sur « Citer » à côté d\'un article pour l\'insérer dans votre texte.\n<div class="tour-step-badge">✅ Citation insérée dans le paragraphe</div>',
            side: 'left',
        },
        onHighlightStarted: () =>
            clickTourButton('[data-tour="reference-cite-btn"]'),
    },
    // 33 — Switch to the Bibliography tab (on the articles page)
    {
        url: (ctx) => `/projects/${ctx.projectId}/articles`,
        element: '[data-tour="bibliography-tab-btn"]',
        popover: {
            title: 'Onglet Bibliographie',
            description:
                "Les citations que vous insérez dans l'éditeur alimentent automatiquement cette bibliographie, accessible depuis l'onglet « Bibliographie » de la page Articles.",
        },
        onHighlightStarted: () =>
            clickTourButton('[data-tour="bibliography-tab-btn"]'),
    },
    // 34 — Bibliography
    {
        element: '[data-tour="editor-bibliography"]',
        popover: {
            title: 'Bibliographie automatique',
            description:
                'Toutes les références citées dans le texte apparaissent ici, formatées selon votre style bibliographique — plus besoin de les ressaisir.',
            side: 'left',
        },
    },
    // 35 — Change citation style
    {
        element: '[data-tour="citation-style-select"]',
        popover: {
            title: 'Changer de format de citation',
            description:
                'Choisissez APA, Chicago ou Vancouver dans ce menu : toute la bibliographie se reformate instantanément, sans rien ressaisir.\n<div class="tour-step-badge">✅ Format basculé sur Vancouver</div>',
            side: 'left',
        },
        onHighlightStarted: () =>
            changeSelectValue(
                '[data-tour="citation-style-select"]',
                'vancouver',
            ),
    },
    // 36 — End / user menu
    {
        element: '[data-tour="user-menu"]',
        popover: {
            title: 'Tutoriel terminé !',
            description:
                'Vous connaissez maintenant toutes les fonctionnalités clés de Rewio.\n\nRetrouvez ce tutoriel à tout moment depuis votre menu utilisateur. Bonne recherche !',
            side: 'top',
            align: 'end',
            nextBtnText: 'Terminer ✓',
        },
    },
];

// ── Module-level navigate listener (registered once, survives layout changes) ─

let listenerRegistered = false;

// True from the moment a driver instance is built until the tour genuinely
// ends (closed or completed) — NOT reset on a mid-tour page navigation.
// Guards startTour() against AppLayout's auto-start firing again when it
// remounts after navigating from a sidebar-less layout (e.g. the editor)
// back to an AppLayout page while a resume is already in flight.
let tourActive = false;

function ensureNavigateListener(): void {
    if (listenerRegistered || typeof window === 'undefined') {
        return;
    }

    listenerRegistered = true;

    router.on('navigate', () => {
        // A paused tour has no popover to resume — just keep the floating
        // "Reprendre" button available on whichever page the user lands on.
        if (localStorage.getItem(PAUSED_KEY)) {
            nextTick(() => showResumeButton());
        }

        const raw = localStorage.getItem(STORAGE_KEY);

        if (!raw) {
            return;
        }

        try {
            const state: StoredTourState = JSON.parse(raw);
            localStorage.removeItem(STORAGE_KEY);
            nextTick(() =>
                setTimeout(() => buildDriver(state.stepIndex, state.ctx), 600),
            );
        } catch {
            localStorage.removeItem(STORAGE_KEY);
        }
    });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getCsrf(): string {
    return decodeURIComponent(
        document.cookie
            .split('; ')
            .find((c) => c.startsWith('XSRF-TOKEN='))
            ?.split('=')[1] ?? '',
    );
}

async function postDemo(url: string): Promise<Response> {
    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': getCsrf(),
        },
    });
}

async function markComplete(): Promise<void> {
    await postDemo('/tutorial/complete');
}

/** Walks backward from `index` to find the page a step should be shown on — steps without their own `url` stay on whichever page the nearest preceding url'd step opened. */
function pageUrlForStep(
    index: number,
    ctx: TutorialContext,
): string | undefined {
    for (let i = index; i >= 0; i--) {
        const url = STEPS[i]?.url?.(ctx);

        if (url) {
            return url;
        }
    }

    return undefined;
}

function isSamePage(targetUrl: string): boolean {
    const target = new URL(targetUrl, window.location.href).pathname.replace(
        /\/$/,
        '',
    );
    const current = window.location.pathname.replace(/\/$/, '');

    return current === target;
}

function disableNextBtn(label: string): void {
    const btn = document.querySelector<HTMLButtonElement>(
        '.driver-popover-next-btn',
    );

    if (btn) {
        btn.disabled = true;
        btn.textContent = label;
    }
}

/** Clicks the first row of the article list so its detail panel — and any AI suggestion on it — is actually visible, instead of the empty "select an article" placeholder. */
function selectFirstArticle(): void {
    document
        .querySelector<HTMLElement>('[data-tour="articles-list"] li')
        ?.click();
}

/** Clicks the first article on the PDF-associations page so the right-hand panel — the linked/unlinked PDFs you can pick from — is actually visible, instead of the empty "select an article" placeholder. */
function selectFirstAssociationPaper(): void {
    document
        .querySelector<HTMLElement>(
            '[data-tour="associations-paper-list"] button',
        )
        ?.click();
}

/** Clicks a data-tour-tagged button so its real effect (insert, dialog…) is visible during the step, instead of just describing it. */
function clickTourButton(selector: string): void {
    document.querySelector<HTMLElement>(selector)?.click();
}

/** Closes any open Radix/Reka dialog (e.g. the extraction-table insert dialog) via Escape, so it doesn't linger into the next step. */
function dismissAnyOpenDialog(): void {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
}

/** Sets a native <select>'s value and fires a real "change" event, so the component's @change handler runs exactly as if the user had picked the option. */
function changeSelectValue(selector: string, value: string): void {
    const select = document.querySelector<HTMLSelectElement>(selector);

    if (!select) {
        return;
    }

    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
}

// ── Pause / resume ───────────────────────────────────────────────────────────

/** Renders the floating "Reprendre" pill (idempotent) — shown whenever a paused tour exists, including after a fresh page load while paused. */
function showResumeButton(): void {
    if (document.getElementById(RESUME_BTN_ID)) {
        return;
    }

    const btn = document.createElement('button');
    btn.id = RESUME_BTN_ID;
    btn.type = 'button';
    btn.textContent = '▶ Reprendre le tutoriel';

    Object.assign(btn.style, {
        position: 'fixed',
        right: '32px',
        bottom: '32px',
        zIndex: '100000001',
        padding: '10px 18px',
        borderRadius: '999px',
        border: 'none',
        backgroundColor: 'hsl(193 100% 30%)',
        color: '#fff',
        fontSize: '13px',
        fontWeight: '600',
        fontFamily: 'ui-sans-serif,system-ui,sans-serif',
        cursor: 'pointer',
        boxShadow:
            '0 4px 6px -1px rgba(0,0,0,.15),0 10px 24px -6px rgba(0,0,0,.25)',
    } satisfies Partial<CSSStyleDeclaration>);

    btn.addEventListener('click', resumePausedTour);
    document.body.appendChild(btn);
}

function removeResumeButton(): void {
    document.getElementById(RESUME_BTN_ID)?.remove();
}

/** Picks a paused tour back up — navigating to the right page first if the user wandered off elsewhere while paused. */
function resumePausedTour(): void {
    const raw = localStorage.getItem(PAUSED_KEY);

    if (!raw) {
        return;
    }

    localStorage.removeItem(PAUSED_KEY);
    removeResumeButton();

    try {
        const state: StoredTourState = JSON.parse(raw);
        const targetUrl = pageUrlForStep(state.stepIndex, state.ctx);

        if (targetUrl && !isSamePage(targetUrl)) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            router.visit(targetUrl);
        } else {
            buildDriver(state.stepIndex, state.ctx);
        }
    } catch {
        // Malformed state — nothing sensible to resume.
    }
}

// ── Driver ────────────────────────────────────────────────────────────────────

function buildDriver(startIndex: number, ctx: TutorialContext): void {
    tourActive = true;

    let navigating = false;
    let completed = false;

    function proceed(index: number, mergedCtx: TutorialContext): void {
        const step = STEPS[index];
        const nextStep = STEPS[index + 1];
        const targetUrl = nextStep?.url?.(mergedCtx);

        if (step.reloadAfterAction) {
            const state: StoredTourState = {
                stepIndex: index + 1,
                ctx: mergedCtx,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            navigating = true;
            driverObj.destroy();

            function consumeStorage(): void {
                const raw = localStorage.getItem(STORAGE_KEY);

                if (!raw) {
                    return;
                }

                try {
                    const s = JSON.parse(raw) as StoredTourState;
                    localStorage.removeItem(STORAGE_KEY);
                    nextTick(() =>
                        setTimeout(() => buildDriver(s.stepIndex, s.ctx), 300),
                    );
                } catch {
                    localStorage.removeItem(STORAGE_KEY);
                }
            }

            // Primary: fire as soon as Inertia signals success (avoids depending on navigate event for same-page reloads)
            const unlisten = router.on('success', () => {
                unlisten();
                consumeStorage();
            });

            // Fallback: in case the success event doesn't fire within 2.5 s
            setTimeout(() => {
                unlisten();
                consumeStorage();
            }, 2500);

            router.reload();

            return;
        }

        if (targetUrl && !isSamePage(targetUrl)) {
            const state: StoredTourState = {
                stepIndex: index + 1,
                ctx: mergedCtx,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            navigating = true;
            driverObj.destroy();
            router.visit(targetUrl);

            return;
        }

        driverObj.moveNext();
    }

    // Driver.js's built-in "Précédent" only swaps the popover/highlight —
    // it has no notion of our per-step page navigation, so going back to a
    // step shown on a different page left the popover text updated while
    // the page underneath stayed put. Mirrors proceed()'s page-aware logic
    // for the reverse direction.
    function goBack(index: number, mergedCtx: TutorialContext): void {
        if (index <= 0) {
            return;
        }

        const targetUrl = pageUrlForStep(index - 1, mergedCtx);

        if (targetUrl && !isSamePage(targetUrl)) {
            const state: StoredTourState = {
                stepIndex: index - 1,
                ctx: mergedCtx,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            navigating = true;
            driverObj.destroy();
            router.visit(targetUrl);

            return;
        }

        driverObj.movePrevious();
    }

    // Sets the tour aside without ending it: saves the current step so the
    // floating "Reprendre" button can pick it back up later, then tears
    // down the popover/overlay so the page is fully interactive again.
    function pause(index: number, mergedCtx: TutorialContext): void {
        const state: StoredTourState = { stepIndex: index, ctx: mergedCtx };
        localStorage.setItem(PAUSED_KEY, JSON.stringify(state));
        navigating = true; // not a real end — keep onDestroyed from marking complete
        driverObj.destroy();
        showResumeButton();
    }

    const mutableCtx: TutorialContext = { ...ctx };

    // Steps whose onHighlightStarted actually mutates document content
    // (inserts the PRISMA diagram/extraction table/page break/citation)
    // must only run once — now that "Précédent" genuinely revisits a step,
    // going back to one and forward again would otherwise insert a
    // duplicate. Scoped to this buildDriver instance: every step that can
    // replay content lives on the same page (the editor), so this never
    // needs to survive a cross-page navigation.
    const firedOnHighlight = new Set<number>();

    const driverObj = driver({
        showProgress: true,
        progressText: '{{current}} / {{total}}',
        nextBtnText: 'Suivant →',
        prevBtnText: '← Précédent',
        doneBtnText: 'Terminer',
        allowClose: false,
        overlayOpacity: 0.4,
        smoothScroll: true,
        onCloseClick: () => {
            if (!completed) {
                completed = true;
                tourActive = false;
                navigating = true;
                localStorage.removeItem(STORAGE_KEY);
                localStorage.removeItem(PAUSED_KEY);
                removeResumeButton();
                markComplete();
            }

            driverObj.destroy();
        },
        onDestroyed: () => {
            if (!navigating && !completed) {
                completed = true;
                tourActive = false;
                localStorage.removeItem(STORAGE_KEY);
                markComplete();
            }
        },
        onPopoverRender: (popover, { state, config }) => {
            const cs = getComputedStyle(document.documentElement);
            const bg =
                cs.getPropertyValue('--popover').trim() || 'hsl(0 0% 100%)';
            const fg =
                cs.getPropertyValue('--popover-foreground').trim() ||
                'hsl(0 0% 9%)';
            const bc =
                cs.getPropertyValue('--border').trim() || 'hsl(0 0% 90%)';
            const muted =
                cs.getPropertyValue('--muted').trim() || 'hsl(0 0% 96%)';
            const mFg =
                cs.getPropertyValue('--muted-foreground').trim() ||
                'hsl(0 0% 45%)';
            const brand = 'hsl(193 100% 30%)';
            const isDark = document.documentElement.classList.contains('dark');
            const shadow = isDark
                ? '0 4px 6px -1px rgba(0,0,0,.4),0 16px 40px -8px rgba(0,0,0,.65)'
                : '0 4px 6px -1px rgba(0,0,0,.06),0 16px 40px -8px rgba(0,0,0,.14)';

            const total = config.steps?.length ?? 1;
            const current = (state.activeIndex ?? 0) + 1;
            const pct = Math.round((current / total) * 100);

            const st = (el: HTMLElement, props: Record<string, string>) => {
                for (const [k, v] of Object.entries(props)) {
                    (el.style as unknown as Record<string, string>)[k] = v;
                }
            };

            const isCentered = STEPS[state.activeIndex ?? 0]?.centered ?? false;
            popover.wrapper.classList.toggle(
                'driver-popover--centered',
                isCentered,
            );

            // Wrapper — capped to the viewport height; the description
            // scrolls internally so the title and the Suivant/Précédent
            // buttons always stay visible, even for tall content like the
            // embedded PubMed screenshot. Centered steps get a larger box
            // since they're meant to be read comfortably, not glanced at
            // from the corner.
            st(popover.wrapper, {
                backgroundColor: bg,
                color: fg,
                border: `1px solid ${bc}`,
                borderRadius: '16px',
                boxShadow: shadow,
                padding: '22px 22px 18px',
                maxWidth: isCentered ? '640px' : '380px',
                minWidth: '300px',
                maxHeight: isCentered ? 'min(720px, 88vh)' : 'min(560px, 85vh)',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'ui-sans-serif,system-ui,sans-serif',
                zIndex: '100000001',
                overflow: 'hidden',
            });

            // Top accent stripe (brand color)
            let accent =
                popover.wrapper.querySelector<HTMLDivElement>('.tour-accent');

            if (!accent) {
                accent = document.createElement('div');
                accent.className = 'tour-accent';
                popover.wrapper.insertBefore(
                    accent,
                    popover.wrapper.firstChild,
                );
            }

            st(accent, {
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '3px',
                backgroundColor: brand,
                borderRadius: '16px 16px 0 0',
            });

            // Title — step counter pill sits inline next to the title
            // text (e.g. "3/37  Créer un projet"), not stacked above it.
            st(popover.title, {
                display: 'flex',
                alignItems: 'baseline',
                flexWrap: 'wrap',
                gap: '8px',
                fontSize: '15px',
                fontWeight: '650',
                color: fg,
                marginBottom: '8px',
                lineHeight: '1.35',
                letterSpacing: '-0.02em',
                paddingRight: '30px',
                background: 'none',
                marginTop: '4px',
                flexShrink: '0',
            });

            const counter =
                popover.title.querySelector<HTMLSpanElement>(
                    '.tour-step-counter',
                );

            if (counter) {
                st(counter, {
                    fontSize: '11px',
                    fontWeight: '600',
                    color: mFg,
                    backgroundColor: muted,
                    borderRadius: '99px',
                    padding: '2px 8px',
                    letterSpacing: '0.02em',
                    flexShrink: '0',
                });
            }

            // Description — the only part allowed to scroll, so tall
            // content (e.g. an embedded screenshot) never pushes the
            // footer buttons out of the viewport.
            st(popover.description, {
                fontSize: '13px',
                color: mFg,
                lineHeight: '1.7',
                whiteSpace: 'pre-line',
                display: 'block',
                overflowY: 'auto',
                flex: '1 1 auto',
                minHeight: '0',
                background: 'none',
            });

            // Footer — single row. The step counter now lives inline
            // next to the title, so driver.js's own progress pill would
            // just duplicate it (kept in the DOM, hidden).
            st(popover.footer, {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '18px',
                paddingTop: '14px',
                borderTop: `1px solid ${bc}`,
                background: 'none',
                flexShrink: '0',
            });

            st(popover.progress, { display: 'none' });

            // Progress bar (inject below footer)
            let bar = popover.wrapper.querySelector<HTMLDivElement>(
                '.tour-progress-wrap',
            );

            if (!bar) {
                bar = document.createElement('div');
                bar.className = 'tour-progress-wrap';
                popover.wrapper.appendChild(bar);
            }

            st(bar, {
                marginTop: '14px',
                height: '3px',
                backgroundColor: bc,
                borderRadius: '99px',
                overflow: 'hidden',
                flexShrink: '0',
            });
            let fill = bar.querySelector<HTMLDivElement>('.tour-progress-fill');

            if (!fill) {
                fill = document.createElement('div');
                fill.className = 'tour-progress-fill';
                bar.appendChild(fill);
            }

            st(fill, {
                height: '100%',
                width: `${pct}%`,
                backgroundColor: brand,
                borderRadius: '99px',
                transition: 'width 0.35s ease',
            });

            // Navigation buttons wrapper: Précédent, Pause, Suivant.
            // A 3-column grid (1fr / auto / auto-centered / auto / 1fr
            // via the two equal 1fr tracks) — rather than flex
            // space-between — so Pause sits at the exact horizontal
            // center of the footer regardless of how wide
            // Précédent/Suivant's text is (space-between only
            // equalizes the *gaps*, which drifts the middle button off
            // -center whenever the two side buttons differ in width,
            // e.g. "Suivant →" vs "Commencer le tour →"). The two 1fr
            // side columns stay equal width, so Précédent/Suivant are
            // always pinned exactly at the footer's edges too (driver.js's
            // own stylesheet already gives this element flex-grow:1,
            // so it spans the full footer width for the grid to center
            // against).
            st(popover.footerButtons, {
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                gap: '6px',
            });
            st(popover.previousButton, { justifySelf: 'start' });
            st(popover.nextButton, { justifySelf: 'end' });

            const btnBase: Record<string, string> = {
                boxSizing: 'border-box',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                fontSize: '12.5px',
                fontWeight: '500',
                cursor: 'pointer',
                lineHeight: '1',
                padding: '7px 14px',
                whiteSpace: 'nowrap',
            };

            st(popover.previousButton, {
                ...btnBase,
                backgroundColor: 'transparent',
                color: mFg,
                border: `1px solid ${bc}`,
            });
            st(popover.nextButton, {
                ...btnBase,
                backgroundColor: brand,
                color: '#fff',
                border: 'none',
            });

            // Pause button — lets the user set the tour aside to click
            // around and test the real page, without ending it. Sits
            // between Précédent and Suivant. Reuses the same DOM node
            // across renders (querySelector-or-create, like the accent
            // stripe / progress bar above). Icon-only: with three
            // buttons on one row, a text label ("⏸ Pause") pushed the
            // total width past the popover and got clipped by its
            // overflow:hidden — the icon alone leaves plenty of room
            // even for the longest Suivant label ("Commencer le tour →").
            let pauseBtn =
                popover.wrapper.querySelector<HTMLButtonElement>(
                    '.tour-pause-btn',
                );

            if (!pauseBtn) {
                pauseBtn = document.createElement('button');
                pauseBtn.type = 'button';
                pauseBtn.className = 'tour-pause-btn';
            }

            popover.footerButtons.insertBefore(pauseBtn, popover.nextButton);

            pauseBtn.textContent = '⏸';
            pauseBtn.title =
                'Mettre le tutoriel en pause pour tester la page librement';
            pauseBtn.onclick = () => {
                pause(state.activeIndex ?? 0, mutableCtx);
            };
            st(pauseBtn, {
                ...btnBase,
                padding: '7px 10px',
                backgroundColor: 'transparent',
                color: mFg,
                border: `1px dashed ${bc}`,
            });

            st(popover.closeButton, {
                position: 'absolute',
                top: '14px',
                right: '14px',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                color: mFg,
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer',
                lineHeight: '1',
                border: 'none',
                padding: '0',
            });
        },
        steps: STEPS.map((step, index) => ({
            element: step.element,
            onHighlightStarted: step.onHighlightStarted
                ? () => {
                      if (firedOnHighlight.has(index)) {
                          return;
                      }

                      firedOnHighlight.add(index);
                      step.onHighlightStarted?.();
                  }
                : undefined,
            popover: {
                title: `<span class="tour-step-counter">${index + 1}/${STEPS.length}</span>${step.popover.title}`,
                description: step.popover.description,
                side: step.popover.side,
                align: step.popover.align,
                nextBtnText: step.popover.nextBtnText,
                onNextClick: () => {
                    const currentStep = STEPS[index];

                    if (currentStep.onNextAction) {
                        currentStep.onNextAction(mutableCtx).then((extra) => {
                            Object.assign(mutableCtx, extra);
                            proceed(index, mutableCtx);
                        });
                    } else {
                        proceed(index, mutableCtx);
                    }
                },
                onPrevClick: () => {
                    goBack(index, mutableCtx);
                },
            },
        })),
    });

    driverObj.drive(startIndex);
}

// ── Composable ────────────────────────────────────────────────────────────────

export function useTour() {
    ensureNavigateListener();

    // Covers a hard reload while paused (router.on('navigate') only fires
    // on subsequent in-app SPA transitions, not the very first page load).
    if (typeof window !== 'undefined' && localStorage.getItem(PAUSED_KEY)) {
        showResumeButton();
    }

    const page = usePage();

    function getProjectId(): string {
        const props = page.props as any;

        return props.project?.id ?? props.sidebarProjects?.[0]?.id ?? '';
    }

    function startTour(): void {
        // Guards against AppLayout's auto-start firing again when it
        // remounts mid-tour (e.g. navigating back from the sidebar-less
        // editor layout) while a resume is already active or in flight.
        // Also checks PAUSED_KEY directly: tourActive lives in memory and
        // resets on a hard page reload, but a paused tour survives one
        // (it's in localStorage) and must keep blocking auto-start too.
        if (tourActive || localStorage.getItem(PAUSED_KEY)) {
            return;
        }

        const ctx: TutorialContext = { projectId: getProjectId() };

        if (window.location.pathname !== '/projects') {
            const state: StoredTourState = { stepIndex: 0, ctx };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            router.visit('/projects');
        } else {
            buildDriver(0, ctx);
        }
    }

    async function resetTutorial(): Promise<void> {
        localStorage.removeItem(PAUSED_KEY);
        removeResumeButton();
        tourActive = false;
        await postDemo('/tutorial/reset');
    }

    return { startTour, resetTutorial };
}
